import {
  cloneElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";

/**
 * Tooltip próprio, em substituição do `title` nativo do browser (lento a aparecer e sem estilo).
 *
 * Não acrescenta nenhum elemento à volta do filho — clona-o e liga-lhe os handlers de rato/foco
 * (por isso funciona em `<tr>`, `<td>`, botões e inputs sem partir a estrutura da tabela). O balão
 * é desenhado num portal no `body`, com `position: fixed`, para nunca ser cortado pelos
 * contentores com `overflow` da grelha; abre por cima da âncora, vira para baixo quando não há
 * espaço, e é encostado às margens quando ficaria fora do ecrã. Alinha centrado com o elemento
 * âncora — alinhar com o cursor fazia o balão aparecer num sítio diferente a cada hover.
 *
 * Uso: `<Tooltip text="..."><button …>…</button></Tooltip>`. `text` vazio/nulo desativa o balão
 * e o filho renderiza tal e qual — dispensa condicionais no ponto de uso.
 */

interface TooltipProps {
  text?: string | null;
  children: ReactElement<Record<string, unknown>>;
}

// Distância do balão à âncora (inclui a seta), margem mínima às bordas do ecrã e lado da seta.
const GAP = 8;
const MARGIN = 8;
const ARROW = 8;

interface Anchor {
  x: number;
  top: number;
  bottom: number;
}

interface BubblePlacement {
  top: number;
  left: number;
  /** Posição horizontal da seta dentro do balão — aponta ao cursor, mesmo com o balão encostado à margem. */
  arrowLeft: number;
  /** Balão desenhado por baixo da âncora (seta passa para cima). */
  below: boolean;
}

export function Tooltip({ text, children }: TooltipProps) {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [placement, setPlacement] = useState<BubblePlacement | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const id = useId();

  const isOpen = anchor != null && Boolean(text);

  // O balão renderiza primeiro invisível para se poder medir; só depois é posicionado.
  useLayoutEffect(() => {
    if (!isOpen || anchor == null) {
      setPlacement(null);
      return;
    }
    const bubble = bubbleRef.current?.getBoundingClientRect();
    if (!bubble) return;

    const fitsAbove = anchor.top - bubble.height - GAP >= MARGIN;
    const top = fitsAbove
      ? anchor.top - bubble.height - GAP
      : anchor.bottom + GAP;
    const left = Math.max(
      MARGIN,
      Math.min(
        anchor.x - bubble.width / 2,
        window.innerWidth - bubble.width - MARGIN,
      ),
    );
    const arrowLeft = Math.max(
      ARROW,
      Math.min(anchor.x - left - ARROW / 2, bubble.width - ARROW * 2),
    );
    setPlacement({ top, left, arrowLeft, below: !fitsAbove });
  }, [isOpen, anchor]);

  // A posição é fixa e calculada ao abrir — com scroll/resize a âncora move-se e o balão não;
  // fechar é o comportamento honesto.
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setAnchor(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  if (!text) return children;

  const props = children.props;

  const child = cloneElement(children, {
    "aria-describedby": isOpen ? id : undefined,
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      (props.onMouseEnter as ((e: unknown) => void) | undefined)?.(event);
      const rect = event.currentTarget.getBoundingClientRect();
      setAnchor({
        x: rect.left + rect.width / 2,
        top: rect.top,
        bottom: rect.bottom,
      });
    },
    onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
      (props.onMouseLeave as ((e: unknown) => void) | undefined)?.(event);
      setAnchor(null);
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      (props.onFocus as ((e: unknown) => void) | undefined)?.(event);
      const rect = event.currentTarget.getBoundingClientRect();
      setAnchor({
        x: rect.left + rect.width / 2,
        top: rect.top,
        bottom: rect.bottom,
      });
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      (props.onBlur as ((e: unknown) => void) | undefined)?.(event);
      setAnchor(null);
    },
  });

  return (
    <>
      {child}
      {isOpen &&
        createPortal(
          <div
            ref={bubbleRef}
            id={id}
            role="tooltip"
            className="text-xs"
            style={{
              position: "fixed",
              zIndex: 60,
              maxWidth: "20rem",
              padding: "4px 8px",
              borderRadius: "var(--radius-button)",
              backgroundColor: "var(--foreground)",
              color: "var(--background)",
              pointerEvents: "none",
              ...(placement
                ? { top: placement.top, left: placement.left }
                : ({ top: 0, left: 0, visibility: "hidden" } as CSSProperties)),
            }}
          >
            {text}
            {/* Seta: quadrado rodado 45º, metade escondida atrás do balão. */}
            {placement && (
              <div
                style={{
                  position: "absolute",
                  ...(placement.below
                    ? { top: -ARROW / 2 }
                    : { bottom: -ARROW / 2 }),
                  left: placement.arrowLeft,
                  width: ARROW,
                  height: ARROW,
                  backgroundColor: "var(--foreground)",
                  transform: "rotate(45deg)",
                }}
              />
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
